import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  Check,
  Clock3,
  Delete,
  Eraser,
  Lightbulb,
  LoaderCircle,
  Pause,
  Pencil,
  Play,
  Printer,
  Redo2,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trophy,
  Undo2,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { track } from '../lib/telemetry.js'
import {
  createGameState,
  eraseCell,
  inputDigit,
  isComplete,
  redo,
  resetPlayerEntries,
  restoreGameState,
  serializeGameState,
  toggleNote,
  undo,
} from '../lib/gameState.js'
import { Modal } from './Modal.jsx'
import { DailyRecap } from './DailyRecap.jsx'

const TRAINING_CACHE_PREFIX = 'sukoji:training:'
const GAME_SAVE_PREFIX = 'sukoji:game:'
const GAME_SAVE_INDEX_KEY = 'sukoji:game:index'
const MAX_PERSISTED_HISTORY = 100
const MAX_SAVED_GAMES = 12

const labels = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
}

function normalizePayload(payload, mode) {
  const rawPuzzle = payload?.puzzle?.puzzle || payload?.puzzle || payload?.grid || payload
  const puzzleId = rawPuzzle?.id || rawPuzzle?.day || rawPuzzle?.date || rawPuzzle?.seed
    || `${mode}-${payload?.difficulty || rawPuzzle?.difficulty || 'puzzle'}`
  const puzzle = rawPuzzle ? { ...rawPuzzle, id: puzzleId } : rawPuzzle
  const attempt = payload?.attempt || payload?.session || null
  return {
    puzzle,
    day: payload?.day || payload?.date || puzzle?.day || puzzle?.date || puzzle?.id,
    difficulty: payload?.difficulty || puzzle?.difficulty || (mode === 'daily' ? 'medium' : 'easy'),
    attempt,
    result: payload?.result || null,
    completedBoard: payload?.completedBoard || null,
  }
}

function storageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key)
  } catch { /* stockage indisponible */ }
}

function updateSaveIndex(key, remove = false) {
  try {
    const parsed = JSON.parse(localStorage.getItem(GAME_SAVE_INDEX_KEY) || '[]')
    const entries = Array.isArray(parsed)
      ? parsed.filter((entry) => entry?.key && entry.key !== key)
      : []
    if (!remove) entries.unshift({ key, savedAt: Date.now() })
    for (const stale of entries.slice(MAX_SAVED_GAMES)) localStorage.removeItem(stale.key)
    localStorage.setItem(GAME_SAVE_INDEX_KEY, JSON.stringify(entries.slice(0, MAX_SAVED_GAMES)))
  } catch { /* l'index est une optimisation, jamais un prérequis */ }
}

function persistGame(key, state) {
  const compactState = {
    ...state,
    past: state.past.slice(-MAX_PERSISTED_HISTORY),
    future: state.future.slice(0, MAX_PERSISTED_HISTORY),
  }
  let saved = storageSet(key, serializeGameState(compactState))
  if (!saved) {
    saved = storageSet(key, serializeGameState({ ...compactState, past: [], future: [] }))
  }
  if (saved) updateSaveIndex(key)
}

function persistTrainingSession(difficulty, payload, timing) {
  if (!payload || !timing?.startedAt) return false
  return storageSet(
    `${TRAINING_CACHE_PREFIX}${difficulty}`,
    JSON.stringify({
      savedAt: Date.now(),
      startedAt: timing.startedAt,
      pausedAt: timing.pausedAt,
      pausedMs: timing.pausedMs,
      payload,
    }),
  )
}

function removeGameSave(key) {
  storageRemove(key)
  updateSaveIndex(key, true)
}

function completedGameState(rawPuzzle, completedBoard) {
  const state = createGameState(rawPuzzle)
  if (!Array.isArray(completedBoard) || completedBoard.length !== state.puzzle.height) return state
  const values = state.present.values.map((row, rowIndex) => row.map((value, columnIndex) => {
    if (state.puzzle.blocked[rowIndex][columnIndex]) return 0
    const completedValue = Number(completedBoard[rowIndex]?.[columnIndex])
    return Number.isInteger(completedValue) && completedValue > 0 ? completedValue : value
  }))
  return {
    ...state,
    present: {
      values,
      notes: state.present.notes,
    },
  }
}

function formatClock(milliseconds) {
  const total = Math.max(0, Math.floor(milliseconds / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function resultSeconds(result) {
  const milliseconds = result?.elapsedMs ?? result?.elapsed_ms
  if (Number.isFinite(milliseconds)) return milliseconds / 1000
  return result?.elapsedSeconds ?? result?.elapsed_seconds ?? result?.time ?? 0
}

function gameStorageKey(mode, payload) {
  return `${GAME_SAVE_PREFIX}${mode}:${payload.day || payload.puzzle.id}`
}

function firstEditableCell(state) {
  for (let row = 0; row < state.puzzle.height; row += 1) {
    for (let column = 0; column < state.puzzle.width; column += 1) {
      if (!state.puzzle.blocked[row][column] && !state.puzzle.givens[row][column]) {
        return { row, column }
      }
    }
  }
  return { row: 0, column: 0 }
}

function hasPlayerEntries(state) {
  if (!state) return false
  for (let row = 0; row < state.puzzle.height; row += 1) {
    for (let column = 0; column < state.puzzle.width; column += 1) {
      if (state.puzzle.blocked[row][column] || state.puzzle.givens[row][column]) continue
      if (
        state.present.values[row][column]
        || state.present.notes[row][column].length
      ) return true
    }
  }
  return false
}

function Timer({ startedAt, completedAt, localStartedAt, pausedAt, pausedMs = 0 }) {
  const [now, setNow] = useState(Date.now())
  const start = Number(startedAt) || localStartedAt
  const pausedSince = Number(pausedAt) || null
  const end = Number(completedAt) || pausedSince || now
  const elapsed = Math.max(0, end - start - Math.max(0, Number(pausedMs) || 0))
  useEffect(() => {
    if (completedAt || pausedSince) return undefined
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [completedAt, pausedSince])
  return (
    <span
      className={`game-timer ${pausedSince ? 'is-paused' : ''}`}
      aria-label={`Temps écoulé ${formatClock(elapsed)}${pausedSince ? ', chronomètre en pause' : ''}`}
    >
      {pausedSince ? <Pause size={17} /> : <Clock3 size={17} />} {formatClock(elapsed)}
    </span>
  )
}

function Board({ state, selected, onSelect, wrongCells, locked }) {
  const { puzzle } = state
  const relatedKeys = useMemo(() => {
    const keys = new Set()
    const segmentIndexes = puzzle.cellSegments[selected.row]?.[selected.column] || []
    for (const segmentIndex of segmentIndexes) {
      for (const cell of puzzle.segments[segmentIndex].cells) keys.add(`${cell.row}:${cell.column}`)
    }
    return keys
  }, [puzzle, selected])

  return (
    <div
      className="sukoji-board"
      role="grid"
      aria-label="Grille de Sukoji"
      aria-rowcount={puzzle.height}
      aria-colcount={puzzle.width}
      data-grid-size={puzzle.width}
      style={{ '--grid-columns': puzzle.width }}
    >
      {Array.from({ length: puzzle.height }, (_, row) => (
        <div className="sukoji-row" role="row" aria-rowindex={row + 1} key={row}>
          {Array.from({ length: puzzle.width }, (_, column) => {
          const key = `${row}:${column}`
          if (puzzle.blocked[row][column]) {
            return (
              <span
                className="sukoji-cell is-block"
                key={key}
                style={{ '--cell-index': row * puzzle.width + column }}
                role="gridcell"
                aria-colindex={column + 1}
                aria-label={`Ligne ${row + 1}, colonne ${column + 1}, case noire`}
              />
            )
          }
          const given = puzzle.givens[row][column]
          const value = state.present.values[row][column]
          const notes = state.present.notes[row][column]
          const active = row === selected.row && column === selected.column
          const invalid = wrongCells.has(key)
          const contentLabel = given
            ? `, indice ${given}`
            : value
              ? `, valeur ${value}`
              : notes.length
                ? `, notes ${notes.join(', ')}`
                : ', vide'
          const classNames = [
            'sukoji-cell',
            given ? 'is-given' : 'is-editable',
            active ? 'is-selected' : '',
            !active && relatedKeys.has(key) ? 'is-related' : '',
            invalid ? 'has-conflict' : '',
          ].filter(Boolean).join(' ')
          return (
            <button
              type="button"
              role="gridcell"
              aria-colindex={column + 1}
              aria-selected={active}
              aria-readonly={Boolean(given || locked)}
              aria-invalid={invalid || undefined}
              aria-label={`Ligne ${row + 1}, colonne ${column + 1}${contentLabel}${invalid ? ', erreur à corriger' : ''}`}
              className={classNames}
              key={key}
              style={{ '--cell-index': row * puzzle.width + column }}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect({ row, column })}
              data-cell={key}
            >
              {value ? <span className="cell-value">{value}</span> : notes.length ? (
                <span className="cell-notes" aria-hidden="true">
                  {Array.from({ length: puzzle.maxDigit }, (_, index) => (
                    <i key={index}>{notes.includes(index + 1) ? index + 1 : ''}</i>
                  ))}
                </span>
              ) : null}
              {invalid && <span className="conflict-dot" aria-hidden="true" />}
            </button>
          )
          })}
        </div>
      ))}
    </div>
  )
}

function Controls({ state, notesMode, onToggleNotes, onDigit, onErase, onUndo, onRedo, locked }) {
  return (
    <div className="game-controls">
      <div className="tool-row" aria-label="Outils de saisie">
        <button type="button" onClick={onUndo} disabled={locked || !state.past.length} aria-label="Annuler">
          <Undo2 size={19} /><span>Annuler</span>
        </button>
        <button type="button" onClick={onRedo} disabled={locked || !state.future.length} aria-label="Rétablir">
          <Redo2 size={19} /><span>Rétablir</span>
        </button>
        <button type="button" onClick={onErase} disabled={locked} aria-label="Effacer la case">
          <Eraser size={19} /><span>Effacer</span>
        </button>
        <button
          type="button"
          onClick={onToggleNotes}
          disabled={locked}
          className={notesMode ? 'is-active' : ''}
          aria-pressed={notesMode}
          aria-label="Activer les notes"
        >
          <Pencil size={18} /><span>Notes</span>
          {notesMode && <i className="mode-dot" />}
        </button>
      </div>
      <div className="number-pad" aria-label="Pavé numérique">
        {Array.from({ length: state.puzzle.maxDigit }, (_, index) => index + 1).map((digit) => (
          <button
            key={digit}
            type="button"
            onClick={() => onDigit(digit)}
            disabled={locked}
            aria-label={`Saisir ${digit}${notesMode ? ' en note' : ''}`}
          >
            {digit}
          </button>
        ))}
        <button type="button" className="number-pad-delete" onClick={onErase} disabled={locked} aria-label="Effacer">
          <Delete size={22} />
        </button>
      </div>
    </div>
  )
}

export function GamePage({ mode, difficulty = 'easy', archiveDay = null, nickname, navigate, onDailyComplete }) {
  const [payload, setPayload] = useState(null)
  const [game, setGame] = useState(null)
  const [selected, setSelected] = useState({ row: 0, column: 0 })
  const [notesMode, setNotesMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [messageTone, setMessageTone] = useState('error')
  const [wrongCells, setWrongCells] = useState(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [pauseBusy, setPauseBusy] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [result, setResult] = useState(null)
  const [victoryOpen, setVictoryOpen] = useState(false)
  const [generation, setGeneration] = useState(0)
  const localStartedAt = useRef(Date.now())
  const [localTiming, setLocalTiming] = useState(() => ({
    startedAt: localStartedAt.current,
    pausedAt: null,
    pausedMs: 0,
  }))
  const firstMoveTracked = useRef(false)

  useEffect(() => {
    if (!nickname) return undefined
    let active = true
    setLoading(true)
    setError('')
    const load = async () => {
      try {
        let raw
        let trainingTiming = {
          startedAt: Date.now(),
          pausedAt: null,
          pausedMs: 0,
        }
        if (mode === 'training' && generation === 0) {
          const cached = storageGet(`${TRAINING_CACHE_PREFIX}${difficulty}`)
          if (cached) {
            try {
              const parsed = JSON.parse(cached)
              const generatorVersion = Number(parsed.payload?.puzzle?.generatorVersion || 0)
              if (
                parsed.savedAt > Date.now() - 24 * 60 * 60 * 1000
                && generatorVersion >= 5
              ) {
                raw = parsed.payload
                trainingTiming = {
                  startedAt: Number(parsed.startedAt) || trainingTiming.startedAt,
                  pausedAt: Number(parsed.pausedAt) || null,
                  pausedMs: Math.max(0, Number(parsed.pausedMs) || 0),
                }
              }
            } catch { /* ignore damaged local cache */ }
          }
        }
        if (!raw && mode === 'daily') {
          try {
            raw = await api.startDaily(nickname)
          } catch (startError) {
            if (startError?.status !== 401) throw startError
            await api.saveProfile(nickname)
            raw = await api.startDaily(nickname)
          }
        } else if (!raw && mode === 'archive') {
          try {
            raw = await api.startArchive(archiveDay)
          } catch (startError) {
            if (startError?.status !== 401) throw startError
            await api.saveProfile(nickname)
            raw = await api.startArchive(archiveDay)
          }
        } else if (!raw) {
          raw = await api.training(difficulty)
        }
        if (!active) return
        const normalized = normalizePayload(raw, mode)
        if (!normalized.puzzle?.givens) throw new Error('La grille reçue est incomplète.')
        const key = gameStorageKey(mode, normalized)
        const stored = storageGet(key)
        const initial = normalized.result && normalized.completedBoard
          ? completedGameState(normalized.puzzle, normalized.completedBoard)
          : stored
            ? restoreGameState(normalized.puzzle, stored)
            : createGameState(normalized.puzzle)
        setPayload(normalized)
        if (mode === 'training') {
          localStartedAt.current = trainingTiming.startedAt
          setLocalTiming(trainingTiming)
          persistTrainingSession(difficulty, normalized, trainingTiming)
        }
        firstMoveTracked.current = false
        setGame(initial)
        setSelected(firstEditableCell(initial))
        setResult(normalized.result)
        setMessage('')
        setMessageTone('error')
        setWrongCells(new Set())
        setResetOpen(false)
        if (normalized.result || normalized.attempt?.completedAt) {
          setVictoryOpen(true)
          if (normalized.result) onDailyComplete?.({ day: normalized.day, result: normalized.result })
        }
        if (mode === 'daily') {
          track('daily_view', { mode: 'daily', difficulty: 'medium', source: 'app' })
          track('daily_start', { mode: 'daily', difficulty: 'medium', source: 'app' })
        } else if (mode === 'training') {
          track('training_start', { mode: 'training', difficulty: normalized.difficulty, source: 'app' })
        }
      } catch (loadError) {
        if (active) setError(loadError.message || 'Impossible de charger la grille.')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [mode, difficulty, archiveDay, nickname, generation, onDailyComplete])

  useEffect(() => {
    if (!game || !payload) return
    const key = gameStorageKey(mode, payload)
    if (result) removeGameSave(key)
    else persistGame(key, game)
  }, [game, payload, mode, result])

  useEffect(() => {
    if (mode !== 'training' || !payload || result) return
    persistTrainingSession(difficulty, payload, localTiming)
  }, [difficulty, localTiming, mode, payload, result])

  const serverPausedAt = payload?.attempt?.pausedAt
    ?? payload?.attempt?.paused_at
    ?? null
  const serverPausedMs = payload?.attempt?.pausedMs
    ?? payload?.attempt?.paused_ms
    ?? 0
  const pausedAt = mode === 'training' ? localTiming.pausedAt : serverPausedAt
  const pausedMs = mode === 'training' ? localTiming.pausedMs : serverPausedMs
  const isPaused = !result && Boolean(pausedAt)
  const canReset = hasPlayerEntries(game)

  const clearVerificationFeedback = useCallback(() => {
    setWrongCells((current) => {
      if (!current.size) return current
      return new Set()
    })
    setMessage('')
  }, [])

  const moveSelection = useCallback((deltaRow, deltaColumn) => {
    if (!game) return
    let row = selected.row
    let column = selected.column
    const maxSteps = game.puzzle.width * game.puzzle.height
    for (let step = 0; step < maxSteps; step += 1) {
      row = (row + deltaRow + game.puzzle.height) % game.puzzle.height
      column = (column + deltaColumn + game.puzzle.width) % game.puzzle.width
      if (!game.puzzle.blocked[row][column]) {
        setSelected({ row, column })
        requestAnimationFrame(() => document.querySelector(`[data-cell="${row}:${column}"]`)?.focus())
        return
      }
    }
  }, [game, selected])

  const focusSelectedCell = useCallback(() => {
    requestAnimationFrame(() => document.querySelector(`[data-cell="${selected.row}:${selected.column}"]`)?.focus())
  }, [selected])

  const handleDigit = useCallback((digit) => {
    if (
      !game
      || result
      || submitting
      || isPaused
      || !Number.isInteger(digit)
      || digit < 1
      || digit > game.puzzle.maxDigit
    ) return
    if (mode === 'daily' && !firstMoveTracked.current) {
      firstMoveTracked.current = true
      track('daily_first_move', { mode: 'daily', difficulty: 'medium', source: 'app' })
    }
    clearVerificationFeedback()
    setGame((current) => notesMode
      ? toggleNote(current, selected, digit)
      : inputDigit(current, selected, digit))
    focusSelectedCell()
  }, [clearVerificationFeedback, focusSelectedCell, game, isPaused, mode, notesMode, result, selected, submitting])

  const handleErase = useCallback(() => {
    if (!result && !submitting && !isPaused) setGame((current) => current ? eraseCell(current, selected) : current)
    clearVerificationFeedback()
    focusSelectedCell()
  }, [clearVerificationFeedback, focusSelectedCell, isPaused, result, selected, submitting])

  const handleUndo = useCallback(() => {
    if (result || submitting || isPaused) return
    setGame((current) => current ? undo(current) : current)
    setWrongCells(new Set())
    setMessage('')
    focusSelectedCell()
  }, [focusSelectedCell, isPaused, result, submitting])

  const handleRedo = useCallback(() => {
    if (result || submitting || isPaused) return
    setGame((current) => current ? redo(current) : current)
    setWrongCells(new Set())
    setMessage('')
    focusSelectedCell()
  }, [focusSelectedCell, isPaused, result, submitting])

  const handleToggleNotes = useCallback(() => {
    if (result || submitting || isPaused) return
    setNotesMode((enabled) => !enabled)
    focusSelectedCell()
  }, [focusSelectedCell, isPaused, result, submitting])

  useEffect(() => {
    if (!game) return undefined
    const onKeyDown = (event) => {
      if (isPaused || document.querySelector('[role="dialog"]')) return
      const target = event.target
      if (
        target instanceof HTMLElement
        && target.closest('input, textarea, select, a, [contenteditable="true"]')
      ) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        if (event.shiftKey) handleRedo()
        else handleUndo()
        return
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault()
        handleRedo()
        return
      }
      const arrows = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
      if (arrows[event.key]) {
        event.preventDefault()
        moveSelection(...arrows[event.key])
      } else if (/^[1-9]$/.test(event.key)) {
        event.preventDefault()
        handleDigit(Number(event.key))
      } else if (event.key === 'Backspace' || event.key === 'Delete' || event.key === '0') {
        event.preventDefault()
        handleErase()
      } else if (event.key.toLowerCase() === 'n') {
        event.preventDefault()
        handleToggleNotes()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [game, handleDigit, handleErase, handleRedo, handleToggleNotes, handleUndo, isPaused, moveSelection])

  const verify = async () => {
    if (!game || result || submitting || isPaused) return
    setMessage('')
    setMessageTone('error')
    setSubmitting(true)
    if (mode === 'training') {
      const wrong = new Set()
      for (let row = 0; row < game.puzzle.height; row += 1) {
        for (let column = 0; column < game.puzzle.width; column += 1) {
          const value = game.present.values[row][column]
          if (
            !game.puzzle.blocked[row][column]
            && !game.puzzle.givens[row][column]
            && value > 0
            && game.puzzle.solution?.[row]?.[column] !== value
          ) {
            wrong.add(`${row}:${column}`)
          }
        }
      }
      setWrongCells(wrong)
      if (wrong.size) {
        setMessage(`${wrong.size} ${wrong.size > 1 ? 'cases sont à revoir' : 'case est à revoir'}.`)
      } else if (!isComplete(game)) {
        setMessageTone('success')
        setMessage('Tout est correct pour l’instant. Continuez ainsi !')
      } else {
        const completedAt = Date.now()
        const trainingResult = {
          elapsedMs: Math.max(
            0,
            completedAt - localTiming.startedAt - localTiming.pausedMs,
          ),
          completedAt,
          training: true,
        }
        setResult(trainingResult)
        setVictoryOpen(true)
        storageRemove(`${TRAINING_CACHE_PREFIX}${difficulty}`)
        track('training_complete', {
          mode: 'training',
          difficulty,
          source: 'app',
        })
      }
      setSubmitting(false)
      return
    }

    try {
      const board = game.present.values.map((row, rowIndex) =>
        row.map((value, columnIndex) => game.puzzle.blocked[rowIndex][columnIndex] ? null : value),
      )
      let response
      if (mode === 'archive') {
        try {
          response = await api.submitArchive(payload.day, board)
        } catch (submitError) {
          if (submitError?.status !== 401) throw submitError
          await api.saveProfile(nickname)
          response = await api.submitArchive(payload.day, board)
        }
      } else {
        response = await api.submitDaily(board, payload.day)
      }
      const checkedCells = Array.isArray(response.validation?.wrongCells)
        ? response.validation.wrongCells
        : []
      const wrong = new Set(checkedCells
        .filter(({ row, column }) => Number.isInteger(row) && Number.isInteger(column))
        .map(({ row, column }) => `${row}:${column}`))
      setWrongCells(wrong)
      if (response.success || response.completed || response.valid || response.result) {
        const nextResult = response.result || response.attempt || response
        setResult(nextResult)
        setPayload((current) => ({ ...current, attempt: nextResult }))
        setVictoryOpen(true)
        if (mode === 'daily') {
          onDailyComplete?.({ day: payload.day, result: nextResult })
          track('daily_complete', {
            mode: 'daily',
            difficulty: 'medium',
            source: 'app',
          })
        }
      } else if (wrong.size) {
        setMessage(`${wrong.size} ${wrong.size > 1 ? 'cases sont à revoir' : 'case est à revoir'}.`)
      } else if (response.validation?.complete === false) {
        setMessageTone('success')
        setMessage('Tout est correct pour l’instant. Continuez ainsi !')
      } else {
        setMessage(response.message || 'La grille comporte encore une erreur.')
      }
    } catch (submitError) {
      setMessageTone('error')
      setMessage(submitError.message)
    } finally {
      setSubmitting(false)
    }
  }

  const pauseGame = async () => {
    if (!game || result || submitting || pauseBusy || isPaused) return
    setPauseBusy(true)
    setMessage('')
    try {
      if (mode === 'training') {
        const nextTiming = { ...localTiming, pausedAt: Date.now() }
        setLocalTiming(nextTiming)
        persistTrainingSession(difficulty, payload, nextTiming)
      } else {
        const response = mode === 'archive'
          ? await api.pauseArchive(payload.day)
          : await api.pauseDaily(payload.day)
        setPayload((current) => ({
          ...current,
          attempt: response.attempt || current.attempt,
        }))
      }
    } catch (pauseError) {
      setMessageTone('error')
      setMessage(pauseError.message || 'Impossible de mettre la partie en pause.')
    } finally {
      setPauseBusy(false)
    }
  }

  const resumeGame = async () => {
    if (!game || result || submitting || pauseBusy || !isPaused) return
    setPauseBusy(true)
    setMessage('')
    try {
      if (mode === 'training') {
        const resumedAt = Date.now()
        const nextTiming = {
          ...localTiming,
          pausedAt: null,
          pausedMs: localTiming.pausedMs + Math.max(0, resumedAt - localTiming.pausedAt),
        }
        setLocalTiming(nextTiming)
        persistTrainingSession(difficulty, payload, nextTiming)
      } else {
        const response = mode === 'archive'
          ? await api.resumeArchive(payload.day)
          : await api.resumeDaily(payload.day)
        setPayload((current) => ({
          ...current,
          attempt: response.attempt || current.attempt,
        }))
      }
      setMessageTone('neutral')
      setMessage('Partie reprise. Le chronomètre continue.')
      focusSelectedCell()
    } catch (resumeError) {
      setMessageTone('error')
      setMessage(resumeError.message || 'Impossible de reprendre la partie.')
    } finally {
      setPauseBusy(false)
    }
  }

  const requestReset = () => {
    if (!canReset || result || submitting || pauseBusy || isPaused) return
    setResetOpen(true)
  }

  const confirmReset = () => {
    setGame((current) => current ? resetPlayerEntries(current) : current)
    setSelected(firstEditableCell(game))
    setNotesMode(false)
    setWrongCells(new Set())
    setMessageTone('neutral')
    setMessage('Toutes vos saisies ont été effacées. Le chronomètre continue.')
    setResetOpen(false)
    focusSelectedCell()
  }

  const newTraining = () => {
    storageRemove(`${TRAINING_CACHE_PREFIX}${difficulty}`)
    if (payload) removeGameSave(gameStorageKey(mode, payload))
    setResult(null)
    setVictoryOpen(false)
    setWrongCells(new Set())
    setMessage('')
    const startedAt = Date.now()
    localStartedAt.current = startedAt
    setLocalTiming({ startedAt, pausedAt: null, pausedMs: 0 })
    setGeneration((value) => value + 1)
  }

  const quitGame = () => {
    const fallback = mode === 'daily' ? '/' : mode === 'archive' ? '/archives' : '/entrainement'
    if (window.history.state?.sukojiInternalEntry) window.history.back()
    else navigate(fallback, { replace: true })
  }

  const updateDailyProgress = useCallback((extra) => {
    if (mode === 'daily' && result && payload) {
      onDailyComplete?.({ day: payload.day, result, extra })
    }
  }, [mode, onDailyComplete, payload, result])

  if (loading || !nickname) {
    return <main className="game-loading"><LoaderCircle className="spin" size={28} /><p>Préparation de votre grille…</p></main>
  }
  if (error || !game || !payload) {
    return (
      <main className="game-loading game-loading--error">
        <p className="eyebrow">Oups</p><h1>La grille ne veut pas s’ouvrir.</h1><p>{error}</p>
        <button className="button button--primary" type="button" onClick={() => setGeneration((value) => value + 1)}><RefreshCw size={18} /> Réessayer</button>
      </main>
    )
  }

  const startedAt = mode === 'training'
    ? localTiming.startedAt
    : payload.attempt?.startedAt ?? payload.attempt?.started_at
  const completedAt = payload.attempt?.completedAt ?? payload.attempt?.completed_at ?? result?.completedAt
  const difficultyLabel = labels[payload.difficulty] || labels[difficulty] || 'Moyen'
  const isDaily = mode === 'daily'
  const isArchive = mode === 'archive'

  return (
    <main className="game-page">
      <div className="game-topbar section-shell">
        <button
          className="back-button"
          type="button"
          aria-label="Quitter"
          onClick={quitGame}
        >
          <ArrowLeft size={19} /> <span>Quitter</span>
        </button>
        <div className="game-title">
          <p className="eyebrow">{isDaily ? 'Défi du jour' : isArchive ? 'Archives quotidiennes' : 'Entraînement'}</p>
          <h1>{isDaily ? 'La grille d’aujourd’hui' : isArchive ? `Grille du ${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(new Date(`${payload.day}T12:00:00.000Z`))}` : `Niveau ${difficultyLabel.toLowerCase()}`}</h1>
        </div>
        <div className="game-status">
          {isArchive && <button className="print-grid-button" type="button" onClick={() => window.print()} disabled={isPaused || pauseBusy} aria-label="Imprimer cette grille"><Printer size={16} /><span>Imprimer</span></button>}
          <span className="difficulty-pill">{difficultyLabel}</span>
          <Timer
            startedAt={startedAt}
            completedAt={completedAt}
            localStartedAt={localStartedAt.current}
            pausedAt={pausedAt}
            pausedMs={pausedMs}
          />
        </div>
      </div>

      <div className="game-layout section-shell">
        <section className="board-panel">
          <div className="board-instruction">
            <span>{isPaused ? <Pause size={17} /> : notesMode ? <Pencil size={17} /> : <Sparkles size={17} />}</span>
            <p>{isPaused ? 'Votre grille est masquée et le chronomètre est arrêté.' : notesMode ? 'Mode notes actif : ajoutez ou retirez des candidats.' : 'Touchez une case, puis choisissez un nombre.'}</p>
          </div>
          {isPaused ? (
            <div className="game-pause-panel" role="region" aria-labelledby="game-pause-title">
              <span className="game-pause-icon" aria-hidden="true"><Pause size={31} /></span>
              <h2 id="game-pause-title">Partie en pause</h2>
              <p>
                La grille reste cachée jusqu’à votre retour. Le temps de pause ne compte pas.
                {isDaily ? ' Cette tentative apparaîtra « avec aide » dans le classement.' : ''}
              </p>
              <button className="button button--primary" type="button" onClick={resumeGame} disabled={pauseBusy}>
                {pauseBusy ? <LoaderCircle className="spin" size={18} /> : <Play size={18} />}
                {pauseBusy ? 'Reprise…' : 'Reprendre la partie'}
              </button>
            </div>
          ) : (
            <Board
              key={game.puzzle.id}
              state={game}
              selected={selected}
              onSelect={setSelected}
              wrongCells={wrongCells}
              locked={submitting || pauseBusy || !!result}
            />
          )}
          {message && <p className={`game-message ${messageTone === 'error' ? '' : `is-${messageTone}`}`} role="status">{message}</p>}
        </section>

        <aside className="control-panel">
          <div className="control-heading">
            <div><p className="eyebrow">Votre coup</p><h2>Quel nombre ici ?</h2></div>
            <span className="selected-coordinate">L{selected.row + 1} · C{selected.column + 1}</span>
          </div>
          <Controls
            state={game}
            notesMode={notesMode}
            onToggleNotes={handleToggleNotes}
            onDigit={handleDigit}
            onErase={handleErase}
            onUndo={handleUndo}
            onRedo={handleRedo}
            locked={submitting || pauseBusy || isPaused || !!result}
          />
          <div className="game-secondary-actions">
            <button
              className="game-secondary-button"
              type="button"
              onClick={isPaused ? resumeGame : pauseGame}
              disabled={submitting || pauseBusy || !!result}
              aria-pressed={isPaused}
            >
              {pauseBusy ? <LoaderCircle className="spin" size={17} /> : isPaused ? <Play size={17} /> : <Pause size={17} />}
              {pauseBusy ? 'Patientez…' : isPaused ? 'Reprendre' : 'Pause'}
            </button>
            <button className="game-secondary-button" type="button" onClick={requestReset} disabled={!canReset || submitting || pauseBusy || isPaused || !!result}>
              <RotateCcw size={17} /> Réinitialiser
            </button>
          </div>
          <button className="button button--primary button--full verify-button" type="button" onClick={verify} disabled={submitting || pauseBusy || isPaused || !!result}>
            {submitting ? <><LoaderCircle className="spin" size={18} /> Vérification…</> : <><Check size={19} /> Vérifier la grille</>}
          </button>
          <p className="keyboard-hint"><Lightbulb size={15} /> Astuce : la touche N active les notes. Ctrl + Z annule.</p>
        </aside>
      </div>

      <div className={`mobile-controls ${isPaused ? 'mobile-controls--paused' : ''}`}>
        {isPaused ? (
          <button className="button button--primary button--full" type="button" onClick={resumeGame} disabled={pauseBusy}>
            {pauseBusy ? <LoaderCircle className="spin" size={18} /> : <Play size={18} />}
            {pauseBusy ? 'Reprise…' : 'Reprendre la partie'}
          </button>
        ) : (
          <>
            <Controls
              state={game}
              notesMode={notesMode}
              onToggleNotes={handleToggleNotes}
              onDigit={handleDigit}
              onErase={handleErase}
              onUndo={handleUndo}
              onRedo={handleRedo}
              locked={submitting || pauseBusy || !!result}
            />
            <div className="game-secondary-actions">
              <button className="game-secondary-button" type="button" onClick={pauseGame} disabled={submitting || pauseBusy || !!result}>
                <Pause size={17} /> Pause
              </button>
              <button className="game-secondary-button" type="button" onClick={requestReset} disabled={!canReset || submitting || pauseBusy || !!result}>
                <RotateCcw size={17} /> Réinitialiser
              </button>
            </div>
            <button className="button button--primary button--full" type="button" onClick={verify} disabled={submitting || pauseBusy || !!result}>
              {submitting ? 'Vérification…' : 'Vérifier'}
            </button>
          </>
        )}
      </div>

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        eyebrow="Réinitialiser"
        title="Effacer toutes vos saisies ?"
        className="reset-game-modal"
      >
        <p className="victory-copy">
          Les nombres et les notes que vous avez ajoutés seront effacés. Les indices restent en place et le chronomètre ne repart pas à zéro.
        </p>
        <div className="victory-actions">
          <button className="button button--primary button--full" type="button" onClick={confirmReset}>
            <RotateCcw size={18} /> Réinitialiser la grille
          </button>
          <button className="button button--ghost button--full" type="button" onClick={() => setResetOpen(false)}>
            Continuer ma partie
          </button>
        </div>
      </Modal>

      <Modal
        open={victoryOpen}
        onClose={() => setVictoryOpen(false)}
        eyebrow={isDaily ? 'Défi relevé' : isArchive ? 'Archive terminée' : 'Grille terminée'}
        title={isDaily ? 'Bien joué !' : isArchive ? 'Archive résolue !' : 'Excellent entraînement !'}
        className="victory-modal"
      >
        <div className="victory-icon"><Trophy size={34} /></div>
        {isDaily ? (
          <DailyRecap
            day={payload.day}
            result={result}
            nickname={nickname}
            onProgress={updateDailyProgress}
            onLeaderboard={() => navigate(`/classement?jour=${encodeURIComponent(payload.day)}`, { replace: true })}
            onReview={() => setVictoryOpen(false)}
          />
        ) : isArchive ? (
          <>
            <p className="victory-copy">Cette ancienne grille est parfaitement résolue. Elle reste, comme prévu, hors du classement du jour.</p>
            <div className="result-stats">
              <div><span>Votre temps</span><strong>{formatClock(resultSeconds(result) * 1000)}</strong></div>
            </div>
            <div className="victory-actions">
              <button className="button button--primary button--full" type="button" onClick={() => navigate('/archives', { replace: true })}>Retour aux archives</button>
              <button className="button button--ghost button--full" type="button" onClick={() => setVictoryOpen(false)}>Revoir la grille</button>
            </div>
          </>
        ) : (
          <>
            <p className="victory-copy">Cette grille est parfaitement résolue.</p>
            <div className="result-stats">
              <div><span>Votre temps</span><strong>{formatClock(resultSeconds(result) * 1000)}</strong></div>
            </div>
            <div className="victory-actions">
            <button className="button button--primary button--full" type="button" onClick={newTraining}><RotateCcw size={18} /> Nouvelle grille</button>
              <button className="button button--ghost button--full" type="button" onClick={() => setVictoryOpen(false)}>Revoir la grille</button>
            </div>
          </>
        )}
      </Modal>
    </main>
  )
}
